/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  AudioLines, Bot, Hand, Loader2, Mic, MicOff, Phone, PhoneOff, Play, Speech, Square, Timer, Volume2,
} from 'lucide-react';
import {
  formatVoiceTimer,
  VOICE_SESSION_CRITICAL_SECONDS,
  VOICE_SESSION_WARN_SECONDS,
  VOICE_STATE_LABELS,
} from '../../constants/voiceAgent';
import {
  baseLanguage,
  findSttModel,
  findTtsVoice,
  languageName,
  sttModelLabel,
  TTS_VOICE_GROUPS,
  ttsFamily,
  ttsVoiceName,
  VOICE_STT_MODELS,
} from '../../constants/voiceModels';

const WAVE_BARS = 36;
// Bar envelope: taller in the middle, tapering to the ends, with some unevenness.
const WAVE_WEIGHTS = Array.from({ length: WAVE_BARS }, (_, i) => {
  const t = i / (WAVE_BARS - 1);
  return 0.35 + 0.65 * Math.sin(Math.PI * t) * (0.75 + 0.25 * Math.abs(Math.sin(i * 2.3)));
});

/**
 * Live-conversation waveform, the only state indicator while connected. Colour says
 * whose turn it is and motion says what is happening: flat primary bars while
 * the agent listens, bars that follow the mic while the user talks, a dim
 * shimmer while it thinks, animated secondary bars while the agent talks, and
 * flat muted bars when the mic is muted.
 */
const Waveform = ({ state, level }) => {
  const tone = {
    listening: 'bg-primary/40',
    user_speaking: 'bg-primary',
    thinking: 'bg-base-content/25 animate-pulse',
    agent_speaking: 'bg-secondary',
    muted: 'bg-base-content/15',
  }[state] || 'bg-base-content/15';
  return (
    <div className="flex-1 min-w-0 flex items-center justify-between gap-[2px] h-9 overflow-hidden" aria-hidden="true">
      {WAVE_WEIGHTS.map((w, i) => {
        let inner;
        if (state === 'agent_speaking') {
          inner = { className: 'animate-voice-bar', style: { animationDelay: `${((i * 0.37) % 0.9) * -1}s` } };
        } else if (state === 'user_speaking') {
          const wobble = 0.7 + 0.3 * Math.abs(Math.sin(i * 1.7 + level * 0.15));
          inner = { className: 'transition-transform duration-75', style: { transform: `scaleY(${Math.max(0.12, Math.min(1, (level / 100) * 1.5 * wobble))})` } };
        } else {
          inner = { className: '', style: { transform: 'scaleY(0.12)' } };
        }
        return (
          // eslint-disable-next-line react/no-array-index-key
          <span key={i} className="flex items-center w-[3px] shrink-0" style={{ height: `${Math.round(w * 100)}%` }}>
            <span className={`block w-full h-full rounded-full origin-center ${tone} ${inner.className}`} style={inner.style} />
          </span>
        );
      })}
    </div>
  );
};

Waveform.propTypes = { state: PropTypes.string.isRequired, level: PropTypes.number.isRequired };

const deviceOptions = (list) => (
  list.length
    ? list.map((d) => <option key={d.deviceId} value={d.deviceId}>{d.label}</option>)
    : <option value="">System default</option>
);

const SELECT_CLASS = 'w-full h-9 px-3 bg-base-100 border border-base-300 rounded-lg text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none';
const FIELD_LABEL_CLASS = 'flex items-center gap-1.5 text-[11px] font-bold text-base-content/60';
const DETAIL_BADGE_CLASS = 'badge badge-ghost badge-sm h-5 border-base-content/10 text-[10px] font-semibold text-base-content/70 whitespace-nowrap';

/** An empty stored choice means "system default", which Chrome lists as "default". */
const selectedDevice = (id, list) => id || (list.some((d) => d.deviceId === 'default') ? 'default' : list[0]?.deviceId || '');

/** A labelled select (the label wraps the control, so clicking it focuses the select). */
const Field = ({ icon: Icon, label, children }) => (
  <label className="block space-y-1 min-w-0">
    <span className={FIELD_LABEL_CLASS}><Icon size={12} /> {label}</span>
    {children}
  </label>
);

Field.propTypes = { icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired, children: PropTypes.node.isRequired };

/**
 * Plays a voice's sample on the chosen speaker (where the browser can route
 * output). One sample at a time; stops on unmount.
 */
const useSamplePreview = (outputDeviceId) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState('');

  const stop = useCallback(() => {
    const audio = audioRef.current;
    audioRef.current = null;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    setPlaying('');
  }, []);

  const play = useCallback(async (url) => {
    stop();
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => { if (audioRef.current === audio) stop(); };
    audio.onerror = () => { if (audioRef.current === audio) stop(); };
    setPlaying(url);
    try {
      if (outputDeviceId && typeof audio.setSinkId === 'function') await audio.setSinkId(outputDeviceId);
      if (audioRef.current === audio) await audio.play();
    } catch {
      if (audioRef.current === audio) stop();
    }
  }, [outputDeviceId, stop]);

  useEffect(() => stop, [stop]);
  return { playing, play, stop };
};

/** Speech-to-text model with the languages it understands (badges; hover for the full name). */
const SttModelField = ({ voice }) => {
  const model = findSttModel(voice.sttModel);
  return (
    <div className="space-y-1.5 min-w-0">
      <Field icon={AudioLines} label="Speech-to-text model">
        <select
          className={SELECT_CLASS}
          value={voice.sttModel}
          onChange={(e) => voice.setSttModel(e.target.value)}
        >
          {VOICE_STT_MODELS.map((m) => (
            <option key={m.model} value={m.model}>{sttModelLabel(m.model)}</option>
          ))}
        </select>
      </Field>
      {model && (
        <div className="flex flex-wrap gap-1" aria-label="Supported languages">
          {model.languages.map((code) => (
            <span key={code} className={DETAIL_BADGE_CLASS} title={languageName(code)}>{code}</span>
          ))}
        </div>
      )}
    </div>
  );
};

SttModelField.propTypes = { voice: PropTypes.object.isRequired };

/** Agent voice (TTS model), grouped by family and language, with its details and a sample preview. */
const TtsVoiceField = ({ voice, preview }) => {
  const selected = findTtsVoice(voice.ttsVoice);
  const isPlaying = !!selected && preview.playing === selected.sample;
  return (
    <div className="space-y-1.5 min-w-0">
      <Field icon={Speech} label="Agent voice">
        <select
          className={SELECT_CLASS}
          value={voice.ttsVoice}
          onChange={(e) => { preview.stop(); voice.setTtsVoice(e.target.value); }}
        >
          {TTS_VOICE_GROUPS.map((group) => (
            <optgroup key={group.label} label={group.label}>
              {group.voices.map((v) => (
                <option key={v.model} value={v.model}>{`${ttsVoiceName(v.model)} · ${v.gender}`}</option>
              ))}
            </optgroup>
          ))}
        </select>
      </Field>
      {selected && (
        <div className="flex items-center gap-1 min-w-0">
          <div className="flex flex-wrap gap-1 min-w-0">
            <span className={DETAIL_BADGE_CLASS}>{selected.gender}</span>
            <span className={DETAIL_BADGE_CLASS}>{languageName(selected.language)}</span>
            <span className={DETAIL_BADGE_CLASS}>{ttsFamily(selected.model)}</span>
          </div>
          <ControlTip text={isPlaying ? 'Stop the sample' : 'Hear a sample of this voice'} side="left">
            <button
              type="button"
              onClick={() => (isPlaying ? preview.stop() : preview.play(selected.sample))}
              aria-label={isPlaying ? 'Stop the voice sample' : `Play a sample of ${ttsVoiceName(selected.model)}`}
              aria-pressed={isPlaying}
              className={`btn btn-xs h-6 min-h-0 rounded-lg gap-1 font-bold shrink-0 ${isPlaying ? 'btn-primary' : 'btn-ghost border border-base-content/15'}`}
            >
              {isPlaying ? <Square size={10} /> : <Play size={10} />}
              {isPlaying ? 'Stop' : 'Preview'}
            </button>
          </ControlTip>
        </div>
      )}
    </div>
  );
};

TtsVoiceField.propTypes = { voice: PropTypes.object.isRequired, preview: PropTypes.object.isRequired };

/** Warns when the agent voice speaks a language the speech-to-text model does not understand. */
const languageMismatch = (voice) => {
  const ttsLanguage = baseLanguage(findTtsVoice(voice.ttsVoice)?.language);
  const sttLanguages = findSttModel(voice.sttModel)?.languages.map(baseLanguage) || [];
  if (!ttsLanguage || !sttLanguages.length || sttLanguages.includes(ttsLanguage)) return '';
  return `This voice speaks ${languageName(ttsLanguage)}, which ${sttModelLabel(voice.sttModel)} does not transcribe.`;
};

/**
 * Voice conversation settings in two sections, one field per row: "You speak"
 * (microphone, then speech-to-text model) and "Agent speaks" (speaker, then
 * agent voice). A device select is hidden when there is only one device of
 * its kind (it is picked automatically) or, for the speaker, when the browser
 * cannot route output.
 */
const VoiceSettings = ({ voice, showInput, showOutput, preview }) => {
  const { devices, inputDeviceId, outputDeviceId } = voice;
  const mismatch = languageMismatch(voice);
  return (
    <div className="space-y-3">
      <section className="space-y-2" aria-label="Listening">
        <p className="text-[10px] font-black uppercase tracking-wider text-base-content/40">You speak</p>
        <div className="space-y-3">
          {showInput && (
            <Field icon={Mic} label="Microphone">
              <select
                className={SELECT_CLASS}
                value={selectedDevice(inputDeviceId, devices.inputs)}
                onChange={(e) => voice.setInputDevice(e.target.value)}
              >
                {deviceOptions(devices.inputs)}
              </select>
            </Field>
          )}
          <SttModelField voice={voice} />
        </div>
      </section>
      <div className="border-t border-base-content/10" />
      <section className="space-y-2" aria-label="Agent speaking">
        <p className="text-[10px] font-black uppercase tracking-wider text-base-content/40">Agent speaks</p>
        <div className="space-y-3">
          {showOutput && (
            <Field icon={Volume2} label="Speaker">
              <select
                className={SELECT_CLASS}
                value={selectedDevice(outputDeviceId, devices.outputs)}
                onChange={(e) => { preview.stop(); voice.setOutputDevice(e.target.value); }}
              >
                {deviceOptions(devices.outputs)}
              </select>
            </Field>
          )}
          <TtsVoiceField voice={voice} preview={preview} />
        </div>
      </section>
      {mismatch && <p className="text-[11px] font-semibold text-warning">{mismatch}</p>}
    </div>
  );
};

VoiceSettings.propTypes = {
  voice: PropTypes.object.isRequired,
  showInput: PropTypes.bool.isRequired,
  showOutput: PropTypes.bool.isRequired,
  preview: PropTypes.object.isRequired,
};

// Full class names (not built from parts) so Tailwind / DaisyUI keep them in the build.
const TIP_SIDE = { top: 'tooltip-top', right: 'tooltip-right', left: 'tooltip-left' };
const TIP_TONE = { primary: 'tooltip-primary', error: 'tooltip-error', warning: 'tooltip-warning' };

/**
 * DaisyUI tooltip for a bottom-bar control, the same as the row actions on the
 * Service Requests page (`tooltip tooltip-left tooltip-primary` + `data-tip`),
 * so it follows the active theme. Used instead of the native `title`. `side`
 * keeps it inside the modal, which clips overflow: `right` for controls at the
 * left edge, `left` for those at the right. Without `text` only the child renders.
 */
const ControlTip = ({ text = '', side = 'top', tone = 'primary', children }) => (!text ? children : (
  <div
    className={`tooltip ${TIP_SIDE[side]} ${TIP_TONE[tone]} shrink-0 before:text-xs before:font-semibold before:whitespace-nowrap`}
    data-tip={text}
  >
    {children}
  </div>
));

ControlTip.propTypes = {
  text: PropTypes.string,
  side: PropTypes.oneOf(['top', 'right', 'left']),
  tone: PropTypes.oneOf(['primary', 'error', 'warning']),
  children: PropTypes.node.isRequired,
};

/** Time left in the voice session (shown when a session limit is configured). */
const SessionTimer = ({ seconds }) => {
  const critical = seconds <= VOICE_SESSION_CRITICAL_SECONDS;
  const warn = !critical && seconds <= VOICE_SESSION_WARN_SECONDS;
  const tone = critical
    ? 'text-error border-error/30'
    : warn
      ? 'text-warning border-warning/30'
      : 'text-base-content/60 border-base-content/10';
  const minutes = Math.floor(seconds / 60);
  return (
    <ControlTip text="Time left in this conversation" side="left" tone={critical ? 'error' : warn ? 'warning' : 'primary'}>
      <span
        className={`inline-flex items-center gap-1 h-7 px-2 rounded-lg border text-xs font-bold tabular-nums shrink-0 ${tone}`}
        aria-label={`Time remaining ${minutes ? `${minutes} minutes ` : ''}${seconds % 60} seconds`}
      >
        <Timer size={12} /> {formatVoiceTimer(seconds)}
      </span>
    </ControlTip>
  );
};

SessionTimer.propTypes = { seconds: PropTypes.number.isRequired };

/**
 * Conversation controls shared by the start stage and the in-conversation
 * composer: the start / end button, device preparation and the voice settings.
 *
 * Settings: each tap to start asks for microphone permission (once), lists
 * the devices and opens the voice settings — the speech-to-text model and
 * agent voice, plus the microphone / speaker when there is more than one to
 * choose from. Remembered choices are pre-selected; Connect starts the session.
 */
const useConversationControls = (voice) => {
  const [choosing, setChoosing] = useState(false);
  const [deviceChoice, setDeviceChoice] = useState({ showInput: false, showOutput: false });
  const [preparing, setPreparing] = useState(false);
  const connecting = voice.agentState === 'connecting' || voice.status === 'connecting' || voice.status === 'authenticating';
  const live = voice.status === 'ready' && voice.micOn;
  const canStop = voice.agentState === 'thinking' || voice.agentState === 'agent_speaking';
  const muted = live && voice.muted;
  const displayState = connecting || preparing
    ? 'connecting'
    : muted && !canStop
      ? 'muted'
      : live && VOICE_STATE_LABELS[voice.agentState] ? voice.agentState : 'idle';
  const label = choosing ? 'Choose your voice settings' : VOICE_STATE_LABELS[displayState];

  const toggle = async () => {
    if (live || connecting) {
      voice.stop();
      return;
    }
    voice.primeAudio(); // inside the click: lets audio play after the permission prompt
    setPreparing(true);
    const prepared = await voice.prepareDevices();
    setPreparing(false);
    if (!prepared) return; // permission refused — the error is shown below
    setDeviceChoice({ showInput: prepared.showInput, showOutput: prepared.showOutput });
    setChoosing(true);
  };

  const startWithChoice = () => {
    voice.primeAudio();
    setChoosing(false);
    voice.start();
  };

  return {
    choosing,
    deviceChoice,
    cancelChoice: () => setChoosing(false),
    preparing,
    connecting,
    live,
    canStop,
    muted,
    displayState,
    label,
    toggle,
    startWithChoice,
  };
};

/** Voice settings shown before a conversation connects: devices (when there is a choice), STT model and agent voice. */
const DeviceChoice = ({ voice, choice, onCancel, onStart }) => {
  const preview = useSamplePreview(voice.outputSelectable ? voice.outputDeviceId : '');
  return (
    <div className="space-y-3 rounded-xl border border-base-content/10 bg-base-200/40 p-3 text-left">
      <p className="text-xs font-semibold text-base-content/70">How should this voice conversation sound?</p>
      <VoiceSettings voice={voice} showInput={choice.showInput} showOutput={choice.showOutput} preview={preview} />
      <div className="grid grid-cols-2 gap-2">
        <button type="button" className="btn btn-sm btn-primary rounded-lg gap-1.5 w-full" onClick={() => { preview.stop(); onStart(); }}>
          <Mic size={14} /> Connect
        </button>
        <button type="button" className="btn btn-sm btn-ghost border border-base-content/15 rounded-lg w-full" onClick={() => { preview.stop(); onCancel(); }}>
          Cancel
        </button>
      </div>
    </div>
  );
};

DeviceChoice.propTypes = {
  voice: PropTypes.object.isRequired,
  choice: PropTypes.shape({ showInput: PropTypes.bool, showOutput: PropTypes.bool }).isRequired,
  onCancel: PropTypes.func.isRequired,
  onStart: PropTypes.func.isRequired,
};

/**
 * Start stage, centred in the chat area while no conversation is live and
 * nothing has been said yet: the agent avatar (tap it to start), the
 * connection status, the voice settings (devices, speech-to-text model and agent
 * voice; shown after the avatar is tapped) and the last session error.
 * Once the conversation connects the preview switches to the message list and `VoiceComposer`.
 */
export const VoiceConversationStage = ({ voice, agentName = '' }) => {
  const convo = useConversationControls(voice);
  const busy = convo.connecting || convo.preparing;
  const initials = agentName.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w.charAt(0).toUpperCase()).join('');

  return (
    <div className="h-full overflow-y-auto flex flex-col items-center justify-center gap-5 px-6 py-8 text-center">
      {/* The agent avatar starts the conversation: tap to start, tap again while connecting to cancel.
          Rings ripple out while connecting. */}
      <ControlTip
        text={convo.preparing || convo.choosing ? '' : convo.connecting ? 'Cancel' : 'Start conversation'}
        tone={convo.connecting ? 'error' : 'primary'}
      >
        <button
          type="button"
          onClick={convo.toggle}
          disabled={convo.preparing || convo.choosing}
          aria-label={convo.connecting ? 'Cancel connecting' : `Start a conversation with ${agentName || 'the agent'}`}
          className="group relative flex items-center justify-center w-28 h-28 shrink-0 rounded-full focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/30 disabled:cursor-default"
        >
          {busy && (
            <>
              <span className="absolute inset-0 rounded-full bg-primary/20 animate-ping" aria-hidden="true" />
              <span className="absolute inset-2 rounded-full bg-primary/10 animate-pulse" aria-hidden="true" />
            </>
          )}
          <span
            className={`absolute inset-0 rounded-full border-2 transition-colors ${
              busy ? 'border-primary/40' : 'border-base-content/10 group-hover:border-primary/50'
            }`}
            aria-hidden="true"
          />
          <span
            className={`relative flex items-center justify-center w-20 h-20 rounded-full text-primary transition-all ${
              busy ? 'bg-primary/15' : 'bg-primary/10 group-hover:bg-primary/20 group-hover:shadow-lg group-active:scale-95'
            }`}
          >
            {convo.preparing
              ? <Loader2 size={28} className="animate-spin" />
              : initials ? <span className="text-2xl font-black tracking-tight">{initials}</span> : <Bot size={32} />}
          </span>
        </button>
      </ControlTip>

      <div className="space-y-1 max-w-xs min-w-0">
        <p className="text-base font-bold text-base-content truncate">{agentName || 'AI Agent'}</p>
        <p className="text-xs font-semibold text-base-content/60" aria-live="polite">
          {convo.choosing || convo.preparing
            ? convo.label
            : convo.connecting ? 'Connecting… tap to cancel' : 'Tap the agent to start a conversation'}
        </p>
      </div>

      {convo.choosing && (
        <div className="w-full max-w-md">
          <DeviceChoice voice={voice} choice={convo.deviceChoice} onCancel={convo.cancelChoice} onStart={convo.startWithChoice} />
        </div>
      )}

      {!convo.choosing && !busy && (
        <p className="text-xs text-base-content/40 max-w-xs">
          Speak to hear how this agent responds. Talk over it to interrupt.
        </p>
      )}

      {voice.error && <p className="text-xs text-error max-w-sm" role="alert">{voice.error}</p>}
    </div>
  );
};

VoiceConversationStage.propTypes = {
  voice: PropTypes.object.isRequired,
  agentName: PropTypes.string,
};

/**
 * Voice-mode composer for the agent chat preview, for a spoken conversation
 * with the voice bot: a button that starts / ends the conversation,
 * a status line before it connects and, once connected, only a waveform
 * that shows the state (listening, you speaking, thinking, agent speaking,
 * muted), and an Interrupt button while the agent thinks or speaks. There is no typed input in voice mode.
 * Until the first conversation connects, `VoiceConversationStage` is shown instead.
 *
 * Devices, the speech-to-text model and the agent voice are chosen after tapping
 * to start (in the voice settings); they cannot be changed while connecting or live.
 * While live, the main button ends the conversation and the mute toggle
 * stops sending audio without leaving the session.
 */
const VoiceComposer = ({ voice }) => {
  const {
    choosing, deviceChoice, cancelChoice, preparing, connecting, live, canStop, muted, displayState, label, toggle, startWithChoice,
  } = useConversationControls(voice);
  const levelPercent = Math.min(100, Math.round(Math.sqrt(voice.level || 0) * 220));

  return (
    <div className="px-4 py-3 border-t border-base-content/10 space-y-2.5">
      {choosing ? (
        <DeviceChoice voice={voice} choice={deviceChoice} onCancel={cancelChoice} onStart={startWithChoice} />
      ) : (
        <div className="flex items-center gap-3">
          <ControlTip text={live || connecting ? 'End conversation' : 'Start conversation'} side="right" tone={live || connecting ? 'error' : 'primary'}>
            <button
              type="button"
              onClick={toggle}
              disabled={preparing}
              aria-label={live || connecting ? 'End the conversation with the agent' : 'Start a conversation with the agent'}
              className={`btn btn-circle h-12 w-12 min-h-0 shrink-0 ${live || connecting ? 'btn-error text-error-content' : 'btn-success text-success-content'}`}
            >
              {preparing ? <Loader2 size={18} className="animate-spin" /> : live || connecting ? <PhoneOff size={18} /> : <Phone size={18} />}
            </button>
          </ControlTip>

          {live && (
            <ControlTip text={voice.muted ? 'Unmute (the agent can hear you again)' : 'Mute (stop sending audio)'} side="right" tone={voice.muted ? 'warning' : 'primary'}>
              <button
                type="button"
                onClick={voice.toggleMute}
                aria-pressed={voice.muted}
                aria-label={voice.muted ? 'Unmute microphone' : 'Mute microphone'}
                className={`btn btn-circle btn-sm h-10 w-10 min-h-0 shrink-0 ${voice.muted ? 'btn-warning' : 'btn-ghost border border-base-content/15'}`}
              >
                {voice.muted ? <MicOff size={16} /> : <Mic size={16} />}
              </button>
            </ControlTip>
          )}

          {/* Connected: the waveform alone shows the state (the label stays for screen readers).
              Not connected: the label says what to do (tap to start, connecting…). */}
          {live ? (
            <>
              <Waveform state={displayState} level={muted ? 0 : levelPercent} />
              <span className="sr-only" aria-live="polite">{label}</span>
            </>
          ) : (
            <p className="flex-1 min-w-0 text-sm font-semibold text-base-content/80 truncate" aria-live="polite">{label}</p>
          )}

          {voice.status === 'ready' && voice.remainingSeconds != null && (
            <SessionTimer seconds={voice.remainingSeconds} />
          )}

          {canStop && (
            <ControlTip text="Cut the agent off so you can speak" side="left">
              <button
                type="button"
                onClick={voice.interrupt}
                className="btn btn-sm btn-ghost border border-base-content/10 rounded-lg gap-1.5 font-bold shrink-0"
              >
                <Hand size={12} />
                <span>Interrupt</span>
              </button>
            </ControlTip>
          )}
        </div>
      )}

      {voice.error && <p className="text-xs text-error" role="alert">{voice.error}</p>}
    </div>
  );
};

VoiceComposer.propTypes = {
  voice: PropTypes.shape({
    status: PropTypes.string.isRequired,
    agentState: PropTypes.string.isRequired,
    micOn: PropTypes.bool.isRequired,
    muted: PropTypes.bool.isRequired,
    toggleMute: PropTypes.func.isRequired,
    level: PropTypes.number.isRequired,
    error: PropTypes.string,
    remainingSeconds: PropTypes.number,
    start: PropTypes.func.isRequired,
    stop: PropTypes.func.isRequired,
    interrupt: PropTypes.func.isRequired,
    devices: PropTypes.shape({ inputs: PropTypes.array, outputs: PropTypes.array }).isRequired,
    inputDeviceId: PropTypes.string,
    outputDeviceId: PropTypes.string,
    outputSelectable: PropTypes.bool.isRequired,
    primeAudio: PropTypes.func.isRequired,
    prepareDevices: PropTypes.func.isRequired,
    setInputDevice: PropTypes.func.isRequired,
    setOutputDevice: PropTypes.func.isRequired,
    sttModel: PropTypes.string.isRequired,
    ttsVoice: PropTypes.string.isRequired,
    setSttModel: PropTypes.func.isRequired,
    setTtsVoice: PropTypes.func.isRequired,
  }).isRequired,
};

export default VoiceComposer;
